import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import type { Tables } from '@/integrations/supabase/types';

type Board = Tables<'boards'>;
type Column = Tables<'columns'>;
type Card = Tables<'cards'> & { assigned_profile?: Tables<'profiles'> };
type TagDefinition = Tables<'tag_definitions'>;
type Profile = Tables<'profiles'>;

interface BoardContextType {
  // Board management
  boards: Board[] | undefined;
  currentBoardId: string | null;
  currentBoard: Board | undefined;
  setCurrentBoardId: (boardId: string | null) => void;
  createBoard: (boardData: { title: string; description?: string }) => void;
  updateBoard: (updates: { title?: string; description?: string; is_public?: boolean; default_member_role?: 'owner' | 'admin' | 'editor' | 'viewer' }) => void;
  deleteBoard: () => void;
  boardsLoading: boolean;
  boardsError: Error | null;
  refetchBoards: () => void;
  connectionState: string;
  creatingBoard: boolean;
  updatingBoard: boolean;
  
  // Column management
  columns: Column[] | undefined;
  allColumns: Column[] | undefined; // All columns across all boards
  columnsLoading: boolean;
  createColumn: (columnData: { title: string; position: number; is_completed_column?: boolean }) => void;
  updateColumn: (updates: { columnId: string; title?: string; is_completed_column?: boolean }) => void;
  updateColumnPosition: (updates: { columnId: string; newPosition: number }) => void;
  deleteColumn: (columnId: string) => void;
  creatingColumn: boolean;
  
  // Card management
  cards: Card[] | undefined;
  allCards: Card[] | undefined; // All cards across all boards
  cardsLoading: boolean;
  createCard: (cardData: any) => void;
  updateCard: (updates: any) => void;
  updateCardPosition: (updates: { cardId: string; newColumnId: string; newPosition: number }) => void;
  deleteCard: (cardId: string) => void;
  archiveCard: (cardId: string) => void;
  unarchiveCard: (cardId: string) => void;
  creatingCard: boolean;
  updatingCard: boolean;
  deletingCard: boolean;
  
  // Archive management
  showArchivedCards: boolean;
  setShowArchivedCards: (show: boolean) => void;
  
  // Tag management (now board-scoped)
  tagDefinitions: TagDefinition[] | undefined;
  getTagName: (tagId: string) => string;
  getTagColor: (tagId: string) => string;
  createTagDefinition: (tagData: { name: string; description?: string; color: string }) => void;
  updateTagDefinition: (updates: { tagId: string; updates: { name?: string; description?: string; color?: string } }) => void;
  deleteTagDefinition: (tagId: string) => void;
  
  // Profiles
  profiles: Profile[] | undefined;
  
  // User permissions
  userRole: string | null;
  canEditCards: boolean;
  canEditBoard: boolean;
  
  // Animation coordination
  optimisticUpdateState: Map<string, number>;
  registerOptimisticUpdate: (cardId: string) => void;
  clearOptimisticUpdate: (cardId: string) => void;
}

const BoardContext = createContext<BoardContextType | undefined>(undefined);

export const useBoardContext = () => {
  const context = useContext(BoardContext);
  if (!context) {
    throw new Error('useBoardContext must be used within a BoardContextProvider');
  }
  return context;
};

export const BoardContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [currentBoardId, setCurrentBoardId] = useState<string | null>(null);
  const [showArchivedCards, setShowArchivedCards] = useState(false);
  const activeSubscriptionRef = useRef<any>(null);
  const [connectionState, setConnectionState] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected');
  
  // Animation coordination state
  const [optimisticUpdateState, setOptimisticUpdateState] = useState<Map<string, number>>(new Map());
  const remoteUpdateDebounceRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Animation coordination functions
  const registerOptimisticUpdate = useCallback((cardId: string) => {
    setOptimisticUpdateState(prev => {
      const newMap = new Map(prev);
      newMap.set(cardId, Date.now());
      return newMap;
    });
    
    // Clear optimistic state after settling period
    setTimeout(() => {
      setOptimisticUpdateState(prev => {
        const newMap = new Map(prev);
        newMap.delete(cardId);
        return newMap;
      });
    }, 1000); // 1 second settling period
  }, []);

  const clearOptimisticUpdate = useCallback((cardId: string) => {
    setOptimisticUpdateState(prev => {
      const newMap = new Map(prev);
      newMap.delete(cardId);
      return newMap;
    });
  }, []);

  // Board Management
  const { 
    data: boards, 
    isLoading: boardsLoading, 
    error: boardsError, 
    refetch: refetchBoards 
  } = useQuery({
    queryKey: ['boards', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('boards')
        .select('*')
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Load the list of boards we're members of next
  const { 
    data: boardMembers, 
    isLoading: boardMembersLoading, 
    error: boardMembersError, 
    refetch: refetchBoardMembers 
  } = useQuery({
    queryKey: ['boardMembers', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('board_members')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  const boardIDs = boardMembers?.map(board => board.board_id) || [];
  
  // Load the boards we're members of
  const { 
    data: boardMemberOf, 
    isLoading: boardMemberOfLoading, 
    error: boardMemberOfError, 
    refetch: refetchBoardMemberOf 
  } = useQuery({
    queryKey: ['boardMemberOf', boardIDs],
    queryFn: async () => {
      if (!user?.id || !boardIDs.length) return [];
      const { data, error } = await supabase
        .from('boards')
        .select('*')
        .in('id', boardIDs)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && boardIDs.length > 0,
  });

  // Properly combine the board arrays, filter out any boards that are there twice
    const completeBoardList: Board[] = [
        ...(boards || []),
        ...(boardMemberOf || [])
    ].filter((item, index, self) => self.findIndex((obj) => obj.id === item.id) === index);

  const currentBoard = completeBoardList.find(board => board.id === currentBoardId);

  // Load ALL columns across ALL user boards for dashboard
  const {
    data: allColumns,
    isLoading: allColumnsLoading,
  } = useQuery({
    queryKey: ['allColumns', user?.id],
    queryFn: async () => {
      if (!user?.id || !completeBoardList?.length) return [];
      
      const allBoardIds = completeBoardList.map(board => board.id);
      const { data, error } = await supabase
        .from('columns')
        .select('*')
        .in('board_id', allBoardIds)
        .order('position');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id && !!completeBoardList?.length,
  });

  // Current board columns (for the kanban board view)
  const {
    data: columns,
    isLoading: columnsLoading,
  } = useQuery({
    queryKey: ['columns', currentBoardId],
    queryFn: async () => {
      if (!currentBoardId) return undefined;
      const { data, error } = await supabase
        .from('columns')
        .select('*')
        .eq('board_id', currentBoardId)
        .order('position');
      if (error) throw error;
      return data;
    },
    enabled: !!currentBoardId,
  });

  // Load ALL cards across ALL user boards for dashboard
  const {
    data: allCards,
    isLoading: allCardsLoading,
  } = useQuery({
    queryKey: ['allCards', user?.id, showArchivedCards],
    queryFn: async () => {
      if (!user?.id || !allColumns?.length) return [];
      
      const allColumnIds = allColumns.map(col => col.id);
      
      let query = supabase
        .from('cards')
        .select('*')
        .in('column_id', allColumnIds);

      // Filter archived cards based on showArchivedCards setting
      if (!showArchivedCards) {
        query = query.eq('archived', false);
      }

      const { data, error } = await query.order('position');
      
      if (error) {
        console.error('Error fetching all cards:', error);
        throw error;
      }

      // Get profile data separately for assigned cards
      if (data && data.length > 0) {
        const assignedUserIds = data
          .filter(card => card.assigned_to)
          .map(card => card.assigned_to)
          .filter(Boolean);

        if (assignedUserIds.length > 0) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('*')
            .in('id', assignedUserIds);

          return data.map(card => ({
            ...card,
            assigned_profile: card.assigned_to 
              ? profileData?.find(profile => profile.id === card.assigned_to)
              : undefined
          })) as Card[];
        }
      }
      
      return (data || []).map(card => ({ ...card, assigned_profile: undefined })) as Card[];
    },
    enabled: !!user?.id && !!allColumns?.length,
  });

  // Current board cards (for the kanban board view)
  const {
    data: cards,
    isLoading: cardsLoading,
  } = useQuery({
    queryKey: ['cards', currentBoardId, showArchivedCards],
    queryFn: async () => {
      if (!currentBoardId) return undefined;
      if (!columns || columns.length === 0) return [];
  
      let query = supabase
        .from('cards')
        .select('*')
        .in(
          'column_id',
          columns.map((col) => col.id)
        );

      // Filter archived cards based on showArchivedCards setting
      if (!showArchivedCards) {
        query = query.eq('archived', false);
      }

      const { data, error } = await query.order('position');
  
      if (error) {
        console.error('Error fetching cards:', error);
        throw error;
      }

      // Get profile data separately for assigned cards
      if (data && data.length > 0) {
        const assignedUserIds = data
          .filter(card => card.assigned_to)
          .map(card => card.assigned_to)
          .filter(Boolean);

        if (assignedUserIds.length > 0) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('*')
            .in('id', assignedUserIds);

          return data.map(card => ({
            ...card,
            assigned_profile: card.assigned_to 
              ? profileData?.find(profile => profile.id === card.assigned_to)
              : undefined
          })) as Card[];
        }
      }
  
      return (data || []).map(card => ({ ...card, assigned_profile: undefined })) as Card[];
    },
    enabled: !!currentBoardId && !!columns,
  });

  // Board Management
  const createBoardMutation = useMutation({
    mutationFn: async (boardData: { title: string; description?: string }) => {
      if (!user?.id) throw new Error('User not authenticated');
      const { data, error } = await supabase
        .from('boards')
        .insert([{ ...boardData, owner_id: user.id }])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boards', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['allColumns', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
      toast({
        title: "Success",
        description: "Board created successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error creating board:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create board",
        variant: "destructive",
      });
    },
  });

  const updateBoardMutation = useMutation({
    mutationFn: async (updates: { title?: string; description?: string; is_public?: boolean; default_member_role?: 'owner' | 'admin' | 'editor' | 'viewer' }) => {
      if (!currentBoardId) throw new Error('No board selected');
      const { data, error } = await supabase
        .from('boards')
        .update(updates)
        .eq('id', currentBoardId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boards', user?.id] });
      toast({
        title: "Success",
        description: "Board updated successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error updating board:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update board",
        variant: "destructive",
      });
    },
  });

  const deleteBoardMutation = useMutation({
    mutationFn: async () => {
      if (!currentBoardId) throw new Error('No board selected');
      const { error } = await supabase
        .from('boards')
        .delete()
        .eq('id', currentBoardId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boards', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['allColumns', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
      setCurrentBoardId(null);
      toast({
        title: "Success",
        description: "Board deleted successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error deleting board:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete board",
        variant: "destructive",
      });
    },
  });

  // Column Management
  const createColumnMutation = useMutation({
    mutationFn: async (columnData: { title: string; position: number; is_completed_column?: boolean }) => {
      if (!currentBoardId) throw new Error('No board selected');
      const { data, error } = await supabase
        .from('columns')
        .insert([{ ...columnData, board_id: currentBoardId }])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['columns', currentBoardId] });
      queryClient.invalidateQueries({ queryKey: ['allColumns', user?.id] });
      toast({
        title: "Success",
        description: "Column created successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error creating column:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create column",
        variant: "destructive",
      });
    },
  });

  const updateColumnMutation = useMutation({
    mutationFn: async (updates: { columnId: string; title?: string; is_completed_column?: boolean }) => {
      const { columnId, ...rest } = updates;
      const { data, error } = await supabase
        .from('columns')
        .update(rest)
        .eq('id', columnId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['columns', currentBoardId] });
      queryClient.invalidateQueries({ queryKey: ['cards', currentBoardId] });
      queryClient.invalidateQueries({ queryKey: ['allColumns', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
      toast({
        title: "Success",
        description: "Column updated successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error updating column:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update column",
        variant: "destructive",
      });
    },
  });

  const updateColumnPositionMutation = useMutation({
    mutationFn: async (updates: { columnId: string; newPosition: number }) => {
      const { columnId, newPosition } = updates;
      const { data, error } = await supabase
        .from('columns')
        .update({ position: newPosition })
        .eq('id', columnId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['columns', currentBoardId] });
      queryClient.invalidateQueries({ queryKey: ['allColumns', user?.id] });
      toast({
        title: "Success",
        description: "Column position updated successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error updating column position:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update column position",
        variant: "destructive",
      });
    },
  });

  const deleteColumnMutation = useMutation({
    mutationFn: async (columnId: string) => {
      const { error } = await supabase
        .from('columns')
        .delete()
        .eq('id', columnId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['columns', currentBoardId] });
      queryClient.invalidateQueries({ queryKey: ['cards', currentBoardId] });
      queryClient.invalidateQueries({ queryKey: ['allColumns', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
      toast({
        title: "Success",
        description: "Column deleted successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error deleting column:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete column",
        variant: "destructive",
      });
    },
  });

  // Profiles
  const { data: profilesUnfiltered } = useQuery({
    queryKey: ['profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*');
      if (error) throw error;
      return data;
      },
  }
    );
    // Filter out the profiles with no access to the board
    const profiles: Profile[] = (currentBoard && !currentBoard.is_public) ? profilesUnfiltered?.filter((item) => boardMembers?.find((b => b.board_id === currentBoardId))?.user_id == item.id) || [] : profilesUnfiltered || [];

  // Enhanced Card Management with animation coordination
  const createCardMutation = useMutation({
      mutationFn: async (cardData: any) => {
      // Calculate the last position
      if (!cardData.position) {
              const columnCards = cards?.filter(card => card.column_id === cardData.column_id).sort((a, b) => a.position - b.position) || [];
              cardData.position = columnCards.length > 0 ? columnCards[columnCards.length - 1].position + 1 : 0;
      }
      const { data, error } = await supabase
        .from('cards')
        .insert([cardData])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', currentBoardId] });
      queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
      toast({
        title: "Success",
        description: "Card created successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error creating card:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create card",
        variant: "destructive",
      });
    },
  });

  const updateCardMutation = useMutation({
    mutationFn: async (updates: any) => {
      const { cardId, ...rest } = updates;
      const { data, error } = await supabase
        .from('cards')
        .update(rest.updates)
        .eq('id', cardId);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', currentBoardId] });
      queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
      toast({
        title: "Success",
        description: "Card updated successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error updating card:', error);
        
      toast({
        title: "Error",
        description: error.message || "Failed to update card",
        variant: "destructive",
      });
    },
  });

  const updateCardPositionMutation = useMutation({
    mutationFn: async (updates: { cardId: string; newColumnId: string; newPosition: number }) => {
      const { cardId, newColumnId, newPosition } = updates;

      const { data, error } = await supabase
        .from('cards')
        .update({ 
          column_id: newColumnId, 
          position: newPosition,
          last_edited_by: user?.id 
        })
        .eq('id', cardId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    // Register optimistic update before mutation
    onMutate: async ({ cardId, newColumnId, newPosition }) => {
      // Register this as an optimistic update
      registerOptimisticUpdate(cardId);
      
      const key: [string, string, boolean] =
          ['cards', currentBoardId!, showArchivedCards];
      const allKey: [string, string, boolean] = ['allCards', user?.id, showArchivedCards];
      await queryClient.cancelQueries({ queryKey: key });
      await queryClient.cancelQueries({ queryKey: allKey });

      const previousCards = queryClient.getQueryData<Card[]>(key) || [];
     const previousAllCards = queryClient.getQueryData<Card[]>(allKey) || [];

      const updateFn = (cards: Card[]) => {
          const updated = cards.map(card =>
              card.id === cardId
                  ? { ...card, column_id: newColumnId, position: newPosition, last_edited_by: user?.id }
                  : card
          );

          return [...updated].sort((a, b) => a.position - b.position);
      };

      queryClient.setQueryData(key, updateFn(previousCards));
      queryClient.setQueryData(allKey, updateFn(previousAllCards));

      toast({
          title: "Success",
          description: "Card position updated successfully",
      });

      return { previousCards, previousAllCards };
    },

    onError: (_, __, context) => {
        queryClient.setQueryData(['cards', currentBoardId], context?.previousCards);
        queryClient.setQueryData(['allCards', user?.id], context?.previousAllCards);
    },

    onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ['cards', currentBoardId] });
        queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
    },
  });

  const deleteCardMutation = useMutation({
    mutationFn: async (cardId: string) => {
      const { error } = await supabase
        .from('cards')
        .delete()
        .eq('id', cardId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', currentBoardId] });
      queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
      toast({
        title: "Success",
        description: "Card deleted successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error deleting card:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete card",
        variant: "destructive",
      });
    },
  });

  // Archive card mutations
  const archiveCardMutation = useMutation({
    mutationFn: async (cardId: string) => {
      const { error } = await supabase
        .from('cards')
        .update({ archived: true })
        .eq('id', cardId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', currentBoardId] });
      queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
      toast({
        title: "Success",
        description: "Card archived successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error archiving card:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to archive card",
        variant: "destructive",
      });
    },
  });

  const unarchiveCardMutation = useMutation({
    mutationFn: async (cardId: string) => {
      const { error } = await supabase
        .from('cards')
        .update({ archived: false })
        .eq('id', cardId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cards', currentBoardId] });
      queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
      toast({
        title: "Success",
        description: "Card unarchived successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error unarchiving card:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to unarchive card",
        variant: "destructive",
      });
    },
  });

  // Tag Definitions (now board-scoped)
  const {
    data: tagDefinitions,
    isLoading: tagDefinitionsLoading,
  } = useQuery({
    queryKey: ['tagDefinitions', currentBoardId],
    queryFn: async () => {
      if (!currentBoardId) return [];
      
      const { data, error } = await supabase
        .from('tag_definitions')
        .select('*')
        .eq('board_id', currentBoardId)
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentBoardId,
  });

  // Tag helper functions
  const getTagName = (tagId: string): string => {
    const tag = tagDefinitions?.find(t => t.id === tagId);
    return tag?.name || 'Unknown Tag';
  };

  const getTagColor = (tagId: string): string => {
    const tag = tagDefinitions?.find(t => t.id === tagId);
    return tag?.color || '#3b82f6';
  };

  // Create Tag Definition
  const createTagDefinitionMutation = useMutation({
    mutationFn: async (tagData: { name: string; description?: string; color: string }) => {
      if (!currentBoardId) throw new Error('No board selected');
      
      const { data, error } = await supabase
        .from('tag_definitions')
        .insert([{
          ...tagData,
          board_id: currentBoardId,
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagDefinitions', currentBoardId] });
      toast({
        title: "Success",
        description: "Tag created successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error creating tag definition:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create tag",
        variant: "destructive",
      });
    },
  });

  // Update Tag Definition
  const updateTagDefinitionMutation = useMutation({
    mutationFn: async ({ tagId, updates }: { tagId: string; updates: { name?: string; description?: string; color?: string } }) => {
      const { data, error } = await supabase
        .from('tag_definitions')
        .update(updates)
        .eq('id', tagId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagDefinitions', currentBoardId] });
      toast({
        title: "Success",
        description: "Tag updated successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error updating tag definition:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update tag",
        variant: "destructive",
      });
    },
  });

  // Delete Tag Definition
  const deleteTagDefinitionMutation = useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await supabase
        .from('tag_definitions')
        .delete()
        .eq('id', tagId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tagDefinitions', currentBoardId] });
      toast({
        title: "Success",
        description: "Tag deleted successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error deleting tag definition:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete tag",
        variant: "destructive",
      });
    },
  });

    // Enhanced subscription management with animation coordination and proper type checking
    const handleSubscriptionSwitch = useCallback((boardId: string) => {
        // Cleanup the old subscription first
        if (activeSubscriptionRef.current) {
            console.log("🛑 [REALTIME] Cleaning up previous subscription...");
            supabase.removeChannel(activeSubscriptionRef.current);
            activeSubscriptionRef.current = null;
        }

        setConnectionState('connecting');
        console.log("🟢 [REALTIME] Setting up board-level listener for board:", boardId);

        const setupSubscription = async () => {
            try {
                console.log("🟢 [REALTIME] Setting up enhanced subscription with animation support for board:", boardId);

                const subscription = supabase
                    .channel(`kanban-realtime-${boardId}`)
                    .on(
                        'postgres_changes',
                        {
                            event: '*',
                            schema: 'public',
                            table: 'cards'
                        },
                        (payload) => {
                            console.log("🔄 [REALTIME] Card change received:", payload);
                            
                            // Enhanced remote update handling with animation coordination and proper type checking
                            const updatedCard = payload.new;
                            
                            // **Fix: Add proper type checking for payload.new**
                            if (!updatedCard || typeof updatedCard !== 'object' || !('id' in updatedCard)) {
                                console.log("🚫 [REALTIME] Invalid card data received:", updatedCard);
                                return;
                            }
                            
                            // Skip updates from the current user to avoid conflicts with local drags
                            if ('last_edited_by' in updatedCard && updatedCard.last_edited_by === user?.id) {
                              console.log("🚫 [REALTIME] Skipping self-update for card:", updatedCard.id);
                              return;
                            }
                            
                            // Check if this card has an active optimistic update
                            const cardId = updatedCard.id as string;
                            const optimisticTimestamp = optimisticUpdateState.get(cardId);
                            
                            if (optimisticTimestamp) {
                              const timeSinceOptimistic = Date.now() - optimisticTimestamp;
                              if (timeSinceOptimistic < 1000) { // Within settling period
                                console.log("⏳ [REALTIME] Deferring animation - within optimistic settling period:", {
                                  cardId,
                                  timeSinceOptimistic: `${timeSinceOptimistic}ms`
                                });
                                
                                // Debounce this update to allow optimistic updates to settle
                                const existing = remoteUpdateDebounceRef.current.get(cardId);
                                if (existing) {
                                  clearTimeout(existing);
                                }
                                
                                const timeout = setTimeout(() => {
                                  console.log("🎬 [REALTIME] Triggering deferred animation for card:", cardId);
                                  queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
                                  queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
                                  remoteUpdateDebounceRef.current.delete(cardId);
                                }, 1000 - timeSinceOptimistic);
                                
                                remoteUpdateDebounceRef.current.set(cardId, timeout);
                                return;
                              }
                            }
                            
                            // Immediate animation trigger for cards without optimistic updates
                            console.log("🎬 [REALTIME] Triggering immediate animation for card:", {
                              cardId,
                              title: 'title' in updatedCard ? updatedCard.title : 'Unknown',
                              fromUser: 'last_edited_by' in updatedCard ? updatedCard.last_edited_by : 'Unknown'
                            });
                            
                            // Invalidate queries to trigger animations
                            queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
                            queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
                        }
                    )
                    .on(
                        'postgres_changes',
                        {
                            event: '*',
                            schema: 'public',
                            table: 'columns',
                            filter: `board_id=eq.${boardId}`
                        },
                        (payload) => {
                            console.log("🔄 [REALTIME] Column change received:", payload);
                            queryClient.invalidateQueries({ queryKey: ['columns', boardId] });
                            queryClient.invalidateQueries({ queryKey: ['allColumns', user?.id] });
                            
                            // Also invalidate cards when columns change to ensure consistency
                            queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
                            queryClient.invalidateQueries({ queryKey: ['allCards', user?.id] });
                        }
                    )
                    .subscribe((status) => {
                        console.log("📡 [REALTIME] Subscription status:", status);
                        if (status === 'SUBSCRIBED') {
                            setConnectionState('connected');
                        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                            setConnectionState('disconnected');
                            // Auto-reconnect after delay
                            setTimeout(() => {
                                console.log("🔄 [REALTIME] Auto-reconnecting...");
                                handleSubscriptionSwitch(boardId);
                            }, 3000);
                        }
                    });

                activeSubscriptionRef.current = subscription;
            } catch (error) {
                console.error("❌ [REALTIME] Error setting up subscription:", error);
                setConnectionState('disconnected');
            }
        };

        setupSubscription();
    }, [queryClient, user?.id, optimisticUpdateState]);

    // Cleanup subscription on unmount
    useEffect(() => {
        return () => {
            if (activeSubscriptionRef.current) {
                console.log("🛑 [CLEANUP] Removing subscription on unmount");
                supabase.removeChannel(activeSubscriptionRef.current);
            }
            // Clear all debounce timers
            remoteUpdateDebounceRef.current.forEach(timeout => clearTimeout(timeout));
            remoteUpdateDebounceRef.current.clear();
        };
    }, []);

  // User Role & Permissions
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!currentBoardId || !user?.id) {
        setUserRole(null);
        return;
      }

      // Check if the user has explicit board membership
      let { data: boardMembers, error: boardMembersError } = await supabase
        .from('board_members')
        .select('role')
        .eq('board_id', currentBoardId)
        .eq('user_id', user.id)
        .single();

      if (boardMembersError) {
        console.error('Error fetching board members:', boardMembersError);
      }

      if (boardMembers && boardMembers.role) {
        setUserRole(boardMembers.role);
        return;
      }

      // If no explicit membership, check if the user is the board owner
      const { data: boardsData, error: boardsError } = await supabase
        .from('boards')
        .select('owner_id')
        .eq('id', currentBoardId)
        .single();

      if (boardsError) {
        console.error('Error fetching board:', boardsError);
      }

      if (boardsData && boardsData.owner_id === user.id) {
        setUserRole('owner');
        return;
      }

      // If not owner or explicit member, assume viewer role
      setUserRole('viewer');
      };

    // Switch the subscription
    if (currentBoardId) {
      handleSubscriptionSwitch(currentBoardId);
    }
      fetchUserRole();
  }, [currentBoardId, user?.id, handleSubscriptionSwitch]);

  const canEditCards = userRole === 'owner' || userRole === 'admin' || userRole === 'editor';
  const canEditBoard = userRole === 'owner' || userRole === 'admin';

  const contextValue: BoardContextType = {
    boards: completeBoardList,
    currentBoardId,
    currentBoard,
    setCurrentBoardId,
    createBoard: createBoardMutation.mutate,
    updateBoard: updateBoardMutation.mutate,
    deleteBoard: deleteBoardMutation.mutate,
    boardsLoading,
    boardsError: boardsError as Error | null,
    refetchBoards,
    connectionState,
    creatingBoard: createBoardMutation.isPending,
    updatingBoard: updateBoardMutation.isPending,
    columns,
    allColumns,
    columnsLoading,
    createColumn: createColumnMutation.mutate,
    updateColumn: updateColumnMutation.mutate,
    updateColumnPosition: updateColumnPositionMutation.mutate,
    deleteColumn: deleteColumnMutation.mutate,
    creatingColumn: createColumnMutation.isPending,
    cards,
    allCards,
    cardsLoading,
    createCard: createCardMutation.mutate,
    updateCard: updateCardMutation.mutate,
    updateCardPosition: updateCardPositionMutation.mutate,
    deleteCard: deleteCardMutation.mutate,
    archiveCard: archiveCardMutation.mutate,
    unarchiveCard: unarchiveCardMutation.mutate,
    creatingCard: createCardMutation.isPending,
    updatingCard: updateCardMutation.isPending,
    deletingCard: deleteCardMutation.isPending,
    
    // Archive management
    showArchivedCards,
    setShowArchivedCards,
    
    // Tag management
    tagDefinitions,
    getTagName,
    getTagColor,
    createTagDefinition: createTagDefinitionMutation.mutate,
    updateTagDefinition: updateTagDefinitionMutation.mutate,
    deleteTagDefinition: deleteTagDefinitionMutation.mutate,
    
    // Profiles
    profiles,
    
    // User permissions
    userRole,
    canEditCards,
    canEditBoard,
    
    // Animation coordination
    optimisticUpdateState,
    registerOptimisticUpdate,
    clearOptimisticUpdate,
  };

  return (
    <BoardContext.Provider value={contextValue}>
      {children}
    </BoardContext.Provider>
  );
};
